import { IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { CanWrite, ViewerNotice } from '../components/CanWrite'
import { CopyButton } from '../components/CopyButton'
import { Dialog } from '../components/Dialog'
import { SecretDialog } from '../components/SecretDialog'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import { ApiError } from '../lib/apiClient'
import type { ClientView } from '../lib/apiTypes'
import { formatDateTimeWib } from '../lib/format'
import { describeError, hasFieldError } from '../lib/describeError'
import {
  useClient,
  useCreateClientKey,
  useFinishWebhookSecret,
  useMerchants,
  useRevokeClientKey,
  useRotateWebhookSecret,
  useSetClientMerchants,
  useUpdateClient,
} from '../lib/hooks'
import { rowStatusLabel, rowStatusTone } from '../lib/status'

type SecretReveal = { title: string; label: string; secret: string; note: string }

/**
 * Detail client: identitas, pengaturan webhook, merchant yang diizinkan, dan
 * pengelolaan API key. Secret (API secret dan webhook secret) hanya hidup di
 * dialog satu kali dan tidak disimpan setelah dialog ditutup.
 */
export function ClientDetailPage() {
  const { id = '' } = useParams()
  const client = useClient(id)
  const [reveal, setReveal] = useState<SecretReveal | null>(null)
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null)

  if (client.isPending) {
    return <LoadingState what="detail client" />
  }

  if (client.isError || !client.data) {
    const notFound = client.error instanceof ApiError && client.error.status === 404
    return (
      <>
        <p>
          <Link to="/clients">Kembali ke daftar client</Link>
        </p>
        <ErrorState
          title={notFound ? 'Client tidak ditemukan' : 'Gagal memuat detail client'}
          error={client.error}
          onRetry={notFound ? undefined : () => void client.refetch()}
          retrying={client.isFetching}
        />
      </>
    )
  }

  const data = client.data

  return (
    <>
      <p>
        <Link to="/clients">Kembali ke daftar client</Link>
      </p>

      <div className="page-head">
        <div className="page-head__text">
          <h1>{data.name}</h1>
          <p className="mono">{data.slug}</p>
        </div>
        <Badge tone={rowStatusTone(data.status)}>{rowStatusLabel(data.status)}</Badge>
      </div>

      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}

      <div className="grid-2 grid-2--wide">
        <ClientSettings
          key={`identitas-${data.updated_at}`}
          client={data}
          onFeedback={setFeedback}
        />
        <WebhookSettings
          key={`webhook-${data.updated_at}`}
          client={data}
          onReveal={setReveal}
          onFeedback={setFeedback}
        />
      </div>

      <MerchantAccess
        key={`merchant-${data.updated_at}`}
        client={data}
        onFeedback={setFeedback}
      />

      <ApiKeys client={data} onReveal={setReveal} onFeedback={setFeedback} />

      {reveal ? (
        <SecretDialog
          title={reveal.title}
          label={reveal.label}
          secret={reveal.secret}
          note={reveal.note}
          onClose={() => setReveal(null)}
        />
      ) : null}
    </>
  )
}

function ClientSettings({
  client,
  onFeedback,
}: {
  client: ClientView
  onFeedback: (value: { tone: 'success' | 'danger'; text: string }) => void
}) {
  const update = useUpdateClient(client.id)
  // Nilai awal dibaca dari data server; komponen dipasang ulang lewat key dari
  // updated_at setiap kali server mengembalikan keadaan baru.
  const [name, setName] = useState(client.name)
  const [status, setStatus] = useState(client.status)
  const [rateLimit, setRateLimit] = useState(String(client.rate_limit_per_minute))

  return (
    <section className="card" aria-labelledby="client-settings">
      <div className="card__head">
        <h2 className="card__title" id="client-settings">
          Identitas dan batas laju
        </h2>
        <span className="card__hint">Dibuat {formatDateTimeWib(client.created_at)}</span>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          onFeedback({ tone: 'success', text: '' })
          update.mutate(
            {
              name: name.trim(),
              status,
              rate_limit_per_minute: Number(rateLimit),
            },
            {
              onSuccess: () => onFeedback({ tone: 'success', text: 'Perubahan client disimpan.' }),
              onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
            },
          )
        }}
      >
        <fieldset className="plain-fieldset">
          <div className="form-grid">
            <div className="field">
              <label className="field__label" htmlFor="cd-name">
                Nama
              </label>
              <input
                id="cd-name"
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={hasFieldError(update.error, 'name')}
              />
              <FieldErrorText error={update.error} field="name" />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="cd-status">
                Status
              </label>
              <select
                id="cd-status"
                className="select"
                value={status}
                onChange={(event) => setStatus(event.target.value as ClientView['status'])}
              >
                <option value="ACTIVE">Aktif</option>
                <option value="DISABLED">Nonaktif</option>
              </select>
              <span className="field__hint">
                Client nonaktif ditolak di Client API tanpa menghapus datanya.
              </span>
              <FieldErrorText error={update.error} field="status" />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="cd-rate">
                Batas laju per menit
              </label>
              <input
                id="cd-rate"
                className="input num"
                inputMode="numeric"
                value={rateLimit}
                onChange={(event) => setRateLimit(event.target.value)}
                aria-invalid={hasFieldError(update.error, 'rate_limit_per_minute')}
              />
              <span className="field__hint">
                Jumlah permintaan Client API yang diterima dari client ini setiap menit.
              </span>
              <FieldErrorText error={update.error} field="rate_limit_per_minute" />
            </div>
          </div>
        </fieldset>

        {update.error && !(update.error instanceof ApiError && update.error.fields.length) ? (
          <Alert tone="danger">{describeError(update.error)}</Alert>
        ) : null}

        <CanWrite>
          <div className="state__actions">
            <button type="submit" className="btn btn--primary" disabled={update.isPending}>
              {update.isPending ? 'Menyimpan...' : 'Simpan perubahan'}
            </button>
          </div>
        </CanWrite>
        <ViewerNotice />
      </form>
    </section>
  )
}

function WebhookSettings({
  client,
  onReveal,
  onFeedback,
}: {
  client: ClientView
  onReveal: (value: SecretReveal) => void
  onFeedback: (value: { tone: 'success' | 'danger'; text: string }) => void
}) {
  const update = useUpdateClient(client.id)
  const rotate = useRotateWebhookSecret(client.id)
  const finish = useFinishWebhookSecret(client.id)
  const [url, setUrl] = useState(client.webhook_url)
  const [serverUrl, setServerUrl] = useState(client.webhook_url)

  // Data client bisa berubah setelah mutasi. Komponen juga dipasang ulang lewat
  // key dari updated_at, tetapi bila server mengirim nilai URL baru tanpa
  // remount, isian diselaraskan pada render yang sama. Ketikan yang belum
  // disimpan tidak tersentuh karena pembandingnya nilai dari server.
  if (serverUrl !== client.webhook_url) {
    setServerUrl(client.webhook_url)
    setUrl(client.webhook_url)
  }

  const hasUrl = client.webhook_url.trim() !== ''
  const secretSet = client.webhook_secret_set
  const statusLabel = !hasUrl
    ? 'webhook dimatikan'
    : secretSet
      ? 'secret terpasang'
      : 'secret belum dibuat'

  // Rotasi pada client tanpa secret berarti pembuatan secret awal. Catatan
  // dialog dibedakan karena tidak ada secret lama yang masih berlaku.
  const rotateSecret = (creating: boolean) => {
    rotate.mutate(undefined, {
      onSuccess: (result) => {
        rotate.reset()
        onReveal({
          title: creating ? 'Webhook secret baru' : 'Webhook secret baru (rotasi)',
          label: 'Webhook secret',
          secret: result.secret,
          note: creating
            ? 'Secret ini dipakai client untuk memverifikasi tanda tangan webhook. Simpan di sistem client sebelum menutup dialog.'
            : 'Selama rotasi, webhook ditandatangani dengan secret lama dan baru sekaligus, sehingga client punya waktu memasang secret baru tanpa kehilangan event.',
        })
      },
      onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
    })
  }

  return (
    <section className="card" aria-labelledby="client-webhook">
      <div className="card__head">
        <h2 className="card__title" id="client-webhook">
          Webhook
        </h2>
        <span className="card__hint">{statusLabel}</span>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          update.mutate(
            { webhook_url: url.trim() },
            {
              onSuccess: (result) => {
                // Secret hanya dikirim server saat baru dibuat. Dialog reveal
                // menggantikan pesan sukses biasa supaya nilainya tidak terlewat.
                if (result.webhook_secret) {
                  onReveal({
                    title: 'Webhook secret baru',
                    label: 'Webhook secret',
                    secret: result.webhook_secret,
                    note: 'Secret ini dipakai client untuk memverifikasi tanda tangan webhook. Simpan di sistem client sebelum menutup dialog.',
                  })
                  return
                }
                onFeedback({
                  tone: 'success',
                  text: url.trim() ? 'URL webhook disimpan.' : 'Webhook dimatikan untuk client ini.',
                })
              },
              onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
            },
          )
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="cd-webhook">
            URL webhook
          </label>
          <input
            id="cd-webhook"
            className="input"
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            aria-invalid={hasFieldError(update.error, 'webhook_url')}
          />
          <span className="field__hint">
            Kosongkan untuk mematikan pengiriman webhook. URL divalidasi server; alamat
            internal ditolak kecuali didaftarkan administrator.
          </span>
          <FieldErrorText error={update.error} field="webhook_url" />
        </div>

        <CanWrite>
          <div className="state__actions">
            <button type="submit" className="btn btn--primary" disabled={update.isPending}>
              {update.isPending ? 'Menyimpan...' : 'Simpan URL'}
            </button>
            {secretSet ? (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={rotate.isPending}
                  onClick={() => rotateSecret(false)}
                >
                  <IconRefresh size={18} stroke={1.75} aria-hidden="true" />
                  Rotasi secret
                </button>
                {client.webhook_secret_rotating ? (
                  <button
                    type="button"
                    className="btn"
                    disabled={finish.isPending}
                    onClick={() => {
                      finish.mutate(undefined, {
                        onSuccess: () =>
                          onFeedback({
                            tone: 'success',
                            text: 'Rotasi webhook secret diselesaikan. Secret lama tidak lagi dipakai.',
                          }),
                        onError: (error) =>
                          onFeedback({ tone: 'danger', text: describeError(error) }),
                      })
                    }}
                  >
                    Selesaikan rotasi
                  </button>
                ) : null}
              </>
            ) : hasUrl ? (
              <button
                type="button"
                className="btn"
                disabled={rotate.isPending}
                onClick={() => rotateSecret(true)}
              >
                <IconPlus size={18} stroke={1.75} aria-hidden="true" />
                Buat secret
              </button>
            ) : null}
          </div>
        </CanWrite>
        <ViewerNotice />
      </form>
    </section>
  )
}

function MerchantAccess({
  client,
  onFeedback,
}: {
  client: ClientView
  onFeedback: (value: { tone: 'success' | 'danger'; text: string }) => void
}) {
  const setMerchants = useSetClientMerchants(client.id)
  const merchants = useMerchants()
  const [selected, setSelected] = useState<string[]>(client.merchants)
  const [fallback, setFallback] = useState(client.default_merchant)

  return (
    <section className="card" aria-labelledby="client-merchants">
      <div className="card__head">
        <h2 className="card__title" id="client-merchants">
          Merchant yang diizinkan
        </h2>
        <span className="card__hint">
          {client.merchants.length} merchant terdaftar untuk client ini
        </span>
      </div>

      {merchants.isPending ? <LoadingState what="merchant yang dapat diakses client" /> : merchants.isError ? <ErrorState title="Gagal memuat pilihan merchant" error={merchants.error} onRetry={() => void merchants.refetch()} /> : (merchants.data?.data ?? []).length === 0 ? (
        <EmptyState
          title="Belum ada merchant yang bisa dipilih"
          body="Merchant dibuat lebih dahulu di halaman Merchant. Setelah ada, daftar centang muncul di sini."
          action={
            <Link className="btn" to="/merchants">
              Buka halaman Merchant
            </Link>
          }
        />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            setMerchants.mutate(
              { merchants: selected, default_merchant: fallback },
              {
                onSuccess: () =>
                  onFeedback({ tone: 'success', text: 'Daftar merchant client disimpan.' }),
                onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
              },
            )
          }}
        >
          <div className="field">
            <span className="field__label">Merchant</span>
            <div className="check-list">
              {(merchants.data?.data ?? []).map((merchant) => (
                <label className="checkbox" key={merchant.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(merchant.slug)}
                    onChange={() =>
                      setSelected((current) =>
                        current.includes(merchant.slug)
                          ? current.filter((item) => item !== merchant.slug)
                          : [...current, merchant.slug],
                      )
                    }
                  />
                  {merchant.name} ({merchant.slug})
                </label>
              ))}
            </div>
            <FieldErrorText error={setMerchants.error} field="merchants" />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="cd-default">
              Merchant bawaan
            </label>
            <select
              id="cd-default"
              className="select"
              value={fallback}
              onChange={(event) => setFallback(event.target.value)}
            >
              <option value="">Tidak ada</option>
              {selected.map((slug) => (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              ))}
            </select>
            <FieldErrorText error={setMerchants.error} field="default_merchant" />
          </div>

          <CanWrite>
            <div className="state__actions">
              <button type="submit" className="btn btn--primary" disabled={setMerchants.isPending}>
                {setMerchants.isPending ? 'Menyimpan...' : 'Simpan daftar merchant'}
              </button>
            </div>
          </CanWrite>
          <ViewerNotice />
        </form>
      )}
    </section>
  )
}

function ApiKeys({
  client,
  onReveal,
  onFeedback,
}: {
  client: ClientView
  onReveal: (value: SecretReveal) => void
  onFeedback: (value: { tone: 'success' | 'danger'; text: string }) => void
}) {
  const createKey = useCreateClientKey(client.id)
  const revokeKey = useRevokeClientKey(client.id)
  const [confirmKey, setConfirmKey] = useState<string | null>(null)

  return (
    <section className="card" aria-labelledby="client-keys">
      <div className="card__head">
        <h2 className="card__title" id="client-keys">
          API key
        </h2>
        <span className="card__hint">{client.keys.length} key terdaftar</span>
      </div>

      {client.keys.length === 0 ? (
        <EmptyState
          title="Belum ada API key"
          body="Client belum bisa memanggil Client API tanpa key. Buat satu key, lalu salin secret-nya ke sistem client."
        />
      ) : (
        <div className="rows">
          {client.keys.map((key) => (
            <div className="row-item" key={key.key_id}>
              <div className="row-item__main">
                <span className="row-item__title mono">{key.key_id}</span>
                <span className="row-item__meta">
                  Dibuat {formatDateTimeWib(key.created_at)}
                  {key.last_used_at ? `, terakhir dipakai ${formatDateTimeWib(key.last_used_at)}` : ', belum pernah dipakai'}
                  {key.revoked_at ? `, dicabut ${formatDateTimeWib(key.revoked_at)}` : ''}
                </span>
              </div>
              <div className="state__actions">
                <Badge tone={key.status === 'ACTIVE' ? 'success' : 'neutral'}>
                  {key.status === 'ACTIVE' ? 'Aktif' : 'Dicabut'}
                </Badge>
                <CopyButton value={key.key_id} label={`Salin key id ${key.key_id}`} small />
                {key.status === 'ACTIVE' ? (
                  <CanWrite>
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      onClick={() => setConfirmKey(key.key_id)}
                    >
                      <IconTrash size={16} stroke={1.75} aria-hidden="true" />
                      Cabut
                    </button>
                  </CanWrite>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <CanWrite>
        <div className="state__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={createKey.isPending}
            onClick={() => {
              createKey.mutate(undefined, {
                onSuccess: (result) => {
                  createKey.reset()
                  onReveal({
                    title: 'API secret baru',
                    label: 'API secret',
                    secret: result.secret,
                    note: `Key ${result.key.key_id} langsung aktif. Simpan secret ini di sistem client untuk autentikasi Client API.`,
                  })
                },
                onError: (error) => onFeedback({ tone: 'danger', text: describeError(error) }),
              })
            }}
          >
            <IconPlus size={18} stroke={1.75} aria-hidden="true" />
            Buat API key
          </button>
        </div>
      </CanWrite>
      <ViewerNotice />

      {confirmKey ? (
        <Dialog
          title="Cabut API key"
          onClose={() => setConfirmKey(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmKey(null)}>
                Batal
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={revokeKey.isPending}
                onClick={() => {
                  revokeKey.mutate(confirmKey, {
                    onSuccess: () => {
                      setConfirmKey(null)
                      onFeedback({ tone: 'success', text: 'API key dicabut.' })
                    },
                    onError: (error) => {
                      setConfirmKey(null)
                      onFeedback({ tone: 'danger', text: describeError(error) })
                    },
                  })
                }}
              >
                Cabut key
              </button>
            </>
          }
        >
          <p>
            Key <span className="mono">{confirmKey}</span> berhenti berlaku seketika.
            Permintaan yang memakai key ini ditolak dengan 401 sampai client memakai key
            lain.
          </p>
        </Dialog>
      ) : null}
    </section>
  )
}

